// The result page's offer to add the sharer as a friend (Part A, 4 Nov 2026; spec 0.5). The server accepts the tag only
// for the caller's own proved seat that came in through it, and the sharer accepts on their profile. A sent request is
// remembered for the tag on this device (sessionStorage), so a reload does not offer again.
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/Button';
import { ApiError } from '@/lib/api';
import type { StaffSession } from '@/staff/session';
import { loopClient } from './api';
import { FRIENDS } from './copy';

const KEY = (tag: string) => `rounds.friend.${tag}`;
const sentFor = (tag: string): string | undefined => { try { return sessionStorage.getItem(KEY(tag)) ?? undefined; } catch { return undefined; } };
const rememberSent = (tag: string, name: string) => { try { sessionStorage.setItem(KEY(tag), name); } catch { /* a reload may offer again */ } };

export function AddFriend({ sharerName, shareTag, account }: { sharerName: string; shareTag: string; account?: StaffSession }) {
  const loc = useLocation();
  const [sent, setSent] = useState<string | undefined>(() => sentFor(shareTag));
  const [problem, setProblem] = useState<string>();
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!account || busy) return;
    setBusy(true); setProblem(undefined);
    try {
      const { name } = await loopClient(account.token).addByShare(shareTag);
      rememberSent(shareTag, name); setSent(name);
    } catch (e) {
      setProblem(e instanceof ApiError && typeof e.body.error === 'string' ? e.body.error : FRIENDS.failed);
    } finally { setBusy(false); }
  };
  return (
    <section aria-labelledby="add-friend-heading" data-part="add-friend" className="flex flex-col gap-2 rounded-card bg-surface p-5 shadow-card">
      <h2 id="add-friend-heading" className="display text-2xl text-ink">{sent ? FRIENDS.requested(sent) : FRIENDS.add(sharerName)}</h2>
      {!sent && problem && <p role="alert" className="m-0 font-semibold text-wrong-strong">{problem}</p>}
      {!sent && !problem && (account
        ? <Button type="button" size="lg" className="w-auto" busy={busy} disabled={busy} onClick={add}>{FRIENDS.add(sharerName)}</Button>
        : <Link to={`/sign-in?next=${encodeURIComponent(loc.pathname + loc.search)}`} className="inline-flex min-h-11 w-fit items-center font-semibold text-iv-strong underline underline-offset-2">{FRIENDS.signIn}</Link>)}
    </section>
  );
}
